import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Card, Timestamp } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useDateFormatter } from "@/utils/datetime";
import { escrowApi } from "@/api/escrow";
import { extractErrorMessage } from "@/api/client";

/**
 * Admin-only Escrow.com webhook-delivery diagnostics. Surfaces the registered
 * vs expected webhook URL (a mismatch = PublicBaseUrl changed after the first
 * registration, which silently drops every webhook) and the most recent event
 * ever received. Offers a one-click re-register when the URL is stale.
 */
export function EscrowWebhookDiagnosticsCard() {
  const fmt = useDateFormatter();
  const toast = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["escrow", "diagnostics"],
    queryFn: () => escrowApi.diagnostics(),
  });

  const reRegister = useMutation({
    mutationFn: () => escrowApi.reRegisterWebhook(),
    onSuccess: (data) => {
      toast.success("Webhook re-registered.");
      queryClient.setQueryData(["escrow", "diagnostics"], data);
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });

  const d = query.data;

  return (
    <Card>
      <div className="flex items-center justify-between gap-space-3 mb-space-3">
        <h2 className="font-semibold text-onyx m-0">Webhook diagnostics</h2>
        <Button
          size="sm"
          variant="tertiary"
          onClick={() => void query.refetch()}
          loading={query.isFetching && !query.isLoading}
        >
          Recheck
        </Button>
      </div>

      {query.isLoading ? (
        <p className="text-small text-gray-mid">Loading diagnostics…</p>
      ) : query.isError || !d ? (
        <p className="text-small text-status-error">Could not load webhook diagnostics.</p>
      ) : (
        <div className="flex flex-col gap-space-3">
          {!d.match && (
            <div
              role="alert"
              className="flex flex-col gap-space-2 rounded-lg border border-status-error bg-status-error/10 px-space-3 py-space-2 sm:flex-row sm:items-center sm:justify-between"
            >
              <p className="text-small text-onyx m-0">
                The registered webhook URL doesn't match the expected URL — Escrow.com events
                are not reaching this app. Re-register to fix.
              </p>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => reRegister.mutate()}
                loading={reRegister.isPending}
              >
                Re-register webhook
              </Button>
            </div>
          )}

          <dl className="flex flex-col gap-space-2 m-0">
            <div className="flex items-center justify-between gap-space-3">
              <dt className="text-small text-gray-mid">Status</dt>
              <dd className="m-0">
                <Badge status={d.match ? "finalized" : "error"}>{d.match ? "Matches" : "Mismatch"}</Badge>
              </dd>
            </div>
            <div className="flex flex-col gap-space-1">
              <dt className="text-small text-gray-mid">Registered URL</dt>
              <dd className="text-caption text-onyx break-all m-0">{d.registeredUrl ?? "— none registered —"}</dd>
            </div>
            <div className="flex flex-col gap-space-1">
              <dt className="text-small text-gray-mid">Expected URL</dt>
              <dd className="text-caption text-onyx break-all m-0">{d.expectedUrl}</dd>
            </div>
            <div className="flex items-center justify-between gap-space-3">
              <dt className="text-small text-gray-mid">Most recent event received</dt>
              <dd className="m-0">
                {d.mostRecentEventReceivedAt ? (
                  <Timestamp value={d.mostRecentEventReceivedAt} tz={fmt.tz} className="text-small" />
                ) : (
                  <span className="text-small text-status-warning">Never</span>
                )}
              </dd>
            </div>
          </dl>
        </div>
      )}
    </Card>
  );
}
